import React from 'react';
import Icon from '../../../components/AppIcon';

const IndustryRecognition = () => {
  const certifications = [
    {
      name: 'ISO 27001',
      description: 'Information Security Management',
      authority: 'International Organization for Standardization',
      year: '2021',
      icon: 'Shield',
      color: 'success'
    },
    {
      name: 'SOC 2 Type II',
      description: 'Security, Availability & Confidentiality',
      authority: 'AICPA',
      year: '2022',
      icon: 'Lock',
      color: 'primary'
    },
    {
      name: 'GDPR Compliant',
      description: 'General Data Protection Regulation',
      authority: 'European Union',
      year: '2020',
      icon: 'UserCheck',
      color: 'accent'
    },
    {
      name: 'AWS Partner',
      description: 'Advanced Consulting Partner',
      authority: 'Amazon Web Services',
      year: '2023',
      icon: 'Cloud',
      color: 'warning'
    }
  ];

  const partnerships = [
    {
      name: 'Microsoft',
      type: 'Gold Partner',
      description: 'Azure AI and Machine Learning Solutions',
      focus: 'Cloud AI Implementation',
      logo: 'Microsoft',
      established: '2021'
    },
    {
      name: 'Google Cloud',
      type: 'Premier Partner',
      description: 'AI Platform and TensorFlow Specialization',
      focus: 'ML Operations & Deployment',
      logo: 'Chrome',
      established: '2022'
    },
    {
      name: 'NVIDIA',
      type: 'Preferred Partner',
      description: 'GPU Computing and AI Acceleration',
      focus: 'High-Performance AI Computing',
      logo: 'Zap',
      established: '2020'
    },
    {
      name: 'IBM',
      type: 'Business Partner',
      description: 'Watson AI and Hybrid Cloud Solutions',
      focus: 'Enterprise AI Integration',
      logo: 'Cpu',
      established: '2023'
    }
  ];

  const awards = [
    {
      title: 'AI Excellence Award 2023',
      organization: 'Tech Innovation Council',
      category: 'Best AI Consulting Firm',
      description: 'Recognized for outstanding client outcomes and innovative AI implementations',
      icon: 'Award',
      year: '2023'
    },
    {
      title: 'Cybersecurity Leadership 2022',
      organization: 'CyberSec Institute',
      category: 'Security Innovation',
      description: 'Acknowledged for advancing AI-powered cybersecurity solutions',
      icon: 'Shield',
      year: '2022'
    },
    {
      title: 'Client Choice Award 2023',
      organization: 'Industry Review Board',
      category: 'Customer Satisfaction',
      description: 'Highest client satisfaction ratings in enterprise AI consulting',
      icon: 'Heart',
      year: '2023'
    }
  ];

  const getColorClass = (color) => {
    const colorMap = {
      primary: 'bg-primary text-white',
      success: 'bg-success text-white',
      accent: 'bg-accent text-white',
      warning: 'bg-warning text-white'
    };
    return colorMap[color] || 'bg-primary text-white';
  };

  const getBorderClass = (color) => {
    const borderMap = {
      primary: 'border-primary/20',
      success: 'border-success/20',
      accent: 'border-accent/20',
      warning: 'border-warning/20'
    };
    return borderMap[color] || 'border-primary/20';
  };

  return (
    <section>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Industry Recognition</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Our commitment to excellence is validated by industry certifications, strategic partnerships, and prestigious awards
        </p>
      </div>

      {/* Certifications */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-foreground text-center mb-8">Certifications & Compliance</h3>
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
          {certifications?.map((cert, index) => (
            <div 
              key={index}
              className={`bg-white rounded-xl shadow-soft border-2 ${getBorderClass(cert?.color)} p-6 text-center hover:shadow-elevation transition-smooth group`}
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${getColorClass(cert?.color)} flex items-center justify-center group-hover:scale-110 transition-smooth`}>
                <Icon name={cert?.icon} size={24} />
              </div>
              
              <h4 className="text-lg font-bold text-foreground mb-2">{cert?.name}</h4>
              <p className="text-sm text-muted-foreground mb-3">{cert?.description}</p>
              
              <div className="pt-3 border-t border-border">
                <div className="text-xs font-medium text-foreground">{cert?.authority}</div>
                <div className="text-xs text-muted-foreground">Certified {cert?.year}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strategic Partnerships */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-foreground text-center mb-8">Strategic Partnerships</h3>
        <div className="grid lg:grid-cols-2 gap-8">
          {partnerships?.map((partner, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-soft border border-border p-6 hover:shadow-elevation transition-smooth"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={partner?.logo} size={20} className="text-primary" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-lg font-bold text-foreground">{partner?.name}</h4>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {partner?.type}
                    </span>
                  </div>
                  
                  <p className="text-muted-foreground mb-3">{partner?.description}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium text-foreground">Focus: </span>
                      <span className="text-muted-foreground">{partner?.focus}</span>
                    </div>
                    <div className="text-muted-foreground">
                      Since {partner?.established}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Awards & Recognition */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-foreground text-center mb-8">Awards & Recognition</h3>
        <div className="space-y-6">
          {awards?.map((award, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-soft border border-border p-6 lg:p-8 hover:shadow-elevation transition-smooth"
            >
              <div className="grid lg:grid-cols-4 gap-6 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-warning/20 to-warning/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={award?.icon} size={20} className="text-warning" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-xl font-bold text-foreground">{award?.title}</h4>
                        <span className="px-3 py-1 bg-warning/10 text-warning text-sm font-medium rounded-full">
                          {award?.year}
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <span className="font-medium text-foreground">{award?.organization}</span>
                        <span className="text-muted-foreground"> • {award?.category}</span>
                      </div>
                      
                      <p className="text-muted-foreground">{award?.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="lg:text-right">
                  <div className="inline-flex items-center space-x-2 bg-warning/5 px-4 py-2 rounded-full">
                    <Icon name="Trophy" size={16} className="text-warning" />
                    <span className="text-sm font-medium text-warning">Winner</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recognition Summary */}
      <div className="bg-gradient-to-r from-muted/50 to-background rounded-2xl p-8 lg:p-12">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
            <Icon name="Star" size={32} color="white" />
          </div>
          
          <h3 className="text-2xl font-bold text-foreground mb-4">Trusted by Industry Leaders</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
            Our certifications, partnerships, and awards reflect our unwavering commitment to excellence, 
            security, and innovation. These recognitions validate our position as a trusted leader in the AI consulting space.
          </p>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">4+</div>
              <div className="text-sm text-muted-foreground">Major Certifications</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">4+</div>
              <div className="text-sm text-muted-foreground">Strategic Partners</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">3+</div>
              <div className="text-sm text-muted-foreground">Industry Awards</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">100%</div>
              <div className="text-sm text-muted-foreground">Compliance Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryRecognition;